import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBrain,
    FaBuilding,
    FaCheckCircle,
    FaCode,
    FaFingerprint,
    FaGavel,
    FaPalette,
    FaRocket,
    FaShieldAlt,
    FaUniversity,
    FaUpload
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiBriefcase,
    TiChartLineOutline,
    TiFlash,
    TiGlobeOutline,
    TiLocationOutline,
    TiUserAddOutline
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Careers = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeDepartment, setActiveDepartment] = useState("All");
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploading, success

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  const handleUpload = () => {
    setUploadStatus("uploading");
    setTimeout(() => {
      setUploadStatus("success");
    }, 2000);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const roles = [
    {
      title: "Blockchain Architect",
      brand: "Zero-Blockchain",
      dept: "Engineering",
      location: "London / Remote",
      desc: "Lead the design of our L1 consensus protocols and sovereign engineering frameworks.",
      icon: <FaCode />,
      tags: ["Solidity", "Rust", "L1/L2 Architecture"]
    },
    {
      title: "Architectural Technologist",
      brand: "Machu Design",
      dept: "Design",
      location: "Global Remote",
      desc: "Pioneer the hardware-software symbiosis of Machu Design's architectural ecosystem.",
      icon: <FaBuilding />,
      tags: ["Digital Twins", "IoT", "3D Rendering"]
    },
    {
      title: "Legal Strategy Officer",
      brand: "ReguSync",
      dept: "Legal",
      location: "Manchester / Hybrid",
      desc: "Navigate global regulatory synchronization and institutional compliance protocols.",
      icon: <FaGavel />,
      tags: ["FinTech Law", "ESG Compliance", "RegTech"]
    },
    {
      title: "Senior Software Engineer",
      brand: "Modish0161",
      dept: "Engineering",
      location: "Remote",
      desc: "Join the Modish engine to build the next generation of sovereign software infrastructure.",
      icon: <FaRocket />,
      tags: ["React", "Typescript", "Node.js"]
    },
    {
      title: "Intelligence Analyst",
      brand: "Moo-Lah.io",
      dept: "Finance",
      location: "Global Remote",
      desc: "Drive wealth growth heurists and financial intelligence for the global Moo-Lah syndicate.",
      icon: <TiChartLineOutline />,
      tags: ["Economics", "DeFi Analytics", "Market Trends"]
    },
    {
      title: "Creative Art Director",
      brand: "Zero-Art",
      dept: "Art",
      location: "Manchester / HQ",
      desc: "Curate world-class digital heritage and fine art for the ZeroArtNFT marketplace.",
      icon: <FaPalette />,
      tags: ["Web3 Art", "Curation", "Brand Vision"]
    },
    {
      title: "Crypto Banking Executive",
      brand: "Zero-Bank",
      dept: "Finance",
      location: "Hybrid (Manchester)",
      desc: "Bridge traditional elite banking with decentralised asset intelligence protocols.",
      icon: <FaUniversity />,
      tags: ["PWM", "Crypto Custody", "FinTech"]
    },
    {
      title: "Technical Intelligence Specialist",
      brand: "No-Brainer.io",
      dept: "Product",
      location: "Remote",
      desc: "Simplify complex technical logic for a global audience with No-Brainer intelligence.",
      icon: <FaBrain />,
      tags: ["Technical Writing", "UX Research", "AI Literacy"]
    }
  ];

  const pillars = [
    {
      title: "Sovereign Engineering",
      desc: "We build systems that operate outside traditional fragility, ensuring permanent trust.",
      icon: <FaShieldAlt />
    },
    {
      title: "Institutional Excellence",
      desc: "Our standards are built on the legacy of elite architecture and financial precision.",
      icon: <FaFingerprint />
    },
    {
      title: "Global Synergy",
      desc: "A 12-brand massive alliance where local talent drives world-changing impact.",
      icon: <TiGlobeOutline />
    }
  ];

  const departments = ["All", "Engineering", "Design", "Finance", "Legal", "Art"];
  const filteredRoles = activeDepartment === "All" ? roles : roles.filter(r => r.dept === activeDepartment);

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/careers-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="careersHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Forge the Future"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Build the Foundations of Sovereign Intelligence
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Institutional Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-48">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-12 rounded-[3.5rem] bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-all hover:bg-white/10 group"
            >
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-3xl text-cyan-400 mb-10 group-hover:scale-110 transition-transform">
                {pillar.icon}
              </div>
              <h3 className="text-xl font-bold mb-6 text-white">{pillar.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{pillar.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Roles Header */}
        <div className="mb-20 text-center">
           <div className="flex items-center justify-center gap-3 text-cyan-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-6">
              <TiBriefcase className="text-xl" />
              Open Positions
           </div>
           <h2 className="text-4xl md:text-6xl font-bold mb-12">Search Your <span className="font-zentry italic text-cyan-400 uppercase text-7xl block mt-2">Specialization</span></h2>
           
           <div className="flex flex-wrap justify-center gap-4">
              {departments.map((dept, index) => (
                <button
                  key={index}
                  onClick={() => setActiveDepartment(dept)}
                  className={`px-8 py-3 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all border ${
                    activeDepartment === dept 
                      ? "bg-cyan-600 border-cyan-500 text-black shadow-[0_0_30px_rgba(34,211,238,0.3)]" 
                      : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                  }`}
                >
                  {dept}
                </button>
              ))}
           </div>
        </div>

        {/* Roles Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-48">
           <AnimatePresence mode="popLayout">
              {filteredRoles.map((role, index) => (
                <motion.div
                  layout
                  key={role.title}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="group relative p-12 rounded-[4rem] bg-white/5 border border-white/10 overflow-hidden hover:border-cyan-500/40 transition-all"
                  ref={(el) => (sectionRefs.current[index] = el)}
                >
                  <div className="absolute -top-10 -right-10 text-[180px] text-white/[0.03] group-hover:text-cyan-500/[0.05] transition-colors -rotate-12 pointer-events-none">
                    {role.icon}
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-10">
                       <div className="px-4 py-2 bg-black/60 backdrop-blur-md rounded-xl text-[9px] font-bold text-cyan-400 border border-cyan-500/30 uppercase tracking-widest">
                          {role.brand}
                       </div>
                       <div className="flex items-center gap-2 text-gray-500 text-[10px] uppercase font-bold tracking-widest">
                          <TiLocationOutline size={18} />
                          {role.location}
                       </div>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-white mb-6 leading-tight group-hover:text-cyan-400 transition-colors">
                       {role.title}
                    </h3>
                    
                    <p className="text-gray-300 text-sm leading-relaxed mb-10 line-clamp-2 italic border-l-2 border-cyan-500/30 pl-6">
                       {role.desc}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-12">
                       {role.tags.map((tag, tIndex) => (
                         <span key={tIndex} className="text-[8px] font-bold text-gray-300 border border-white/10 px-3 py-1 rounded-full uppercase tracking-widest">
                            {tag}
                         </span>
                       ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-8 border-t border-white/5">
                       <GlassButton 
                         variant="cyan"
                         size="sm"
                         className="uppercase tracking-widest text-[11px]"
                         onClick={() => window.location.href = `mailto:careers@zeroblockchain.xyz?subject=Elite Application: ${role.title}`}
                       >
                         <div className="flex items-center gap-2">
                           Apply Protocol <TiArrowRight className="text-xl" />
                         </div>
                       </GlassButton>
                       <div className="text-[8px] text-gray-600 font-bold uppercase tracking-[0.2em]">Immediate Req</div>
                    </div>
                  </div>
                </motion.div>
              ))}
           </AnimatePresence>
        </div>

        {/* Submission Protocol Interface */}
        <div className="mb-48 relative p-1 rounded-[5rem] bg-gradient-to-br from-cyan-500/20 to-transparent border border-white/10 overflow-hidden">
           <div className="bg-black/80 backdrop-blur-3xl p-16 md:p-24 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-16 opacity-10">
                 <TiUserAddOutline size={300} className="text-cyan-400" />
              </div>
              
              <div className="max-w-xl relative z-10">
                 <div className="flex items-center gap-3 text-cyan-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-8">
                    <TiFlash className="text-2xl" />
                    Intelligence Entry
                 </div>
                 <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-10">
                    Application <span className="text-cyan-500 font-zentry italic uppercase text-7xl block mt-2">Protocol</span>
                 </h2>
                 <p className="text-gray-300 text-lg leading-relaxed mb-16">
                   Submit your professional identity and technical proof-of-work to join the elite syndicate building the Zero-Ecosystem.
                 </p>
                 
                 <div className="space-y-8">
                    <div className="group">
                       <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4 group-focus-within:text-cyan-400 transition-colors">Digital Identity (Name)</label>
                       <input 
                         type="text" 
                         placeholder="e.g. Satoshi Nakamoto" 
                         className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white focus:border-cyan-500/50 outline-none transition-all placeholder:text-gray-700"
                       />
                    </div>
                    
                    <div 
                      className={`relative border-2 border-dashed rounded-3xl p-12 text-center transition-all cursor-pointer ${
                        uploadStatus === "success" ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/10 bg-white/5 hover:border-cyan-500/30 hover:bg-white/10"
                      }`}
                      onClick={handleUpload}
                    >
                       {uploadStatus === "idle" && (
                         <div className="flex flex-col items-center gap-4">
                            <FaUpload className="text-4xl text-gray-600 group-hover:text-cyan-400 transition-colors" />
                            <span className="text-sm text-gray-500 font-bold uppercase tracking-widest">Drop Dossier / CV here</span>
                            <span className="text-[9px] text-gray-700 uppercase">PDF, MD, or JSON preferred</span>
                         </div>
                       )}
                       {uploadStatus === "uploading" && (
                         <div className="flex flex-col items-center gap-6">
                            <div className="w-12 h-12 rounded-full border-2 border-cyan-500/30 border-t-cyan-500 animate-spin" />
                            <span className="text-sm text-cyan-400 font-bold uppercase tracking-widest animate-pulse">Encrypting Dossier...</span>
                         </div>
                       )}
                       {uploadStatus === "success" && (
                         <div className="flex flex-col items-center gap-4">
                            <FaCheckCircle className="text-4xl text-emerald-500" />
                            <span className="text-sm text-emerald-400 font-bold uppercase tracking-widest text-shadow-glow">Protocol Accepted</span>
                            <span className="text-[9px] text-emerald-600 uppercase">Pending Validation</span>
                         </div>
                       )}
                    </div>
                    
                    <GlassButton
                      className="w-full py-8 text-xl uppercase tracking-[0.2em]"
                      variant="cyan"
                      size="lg"
                    >
                      Initialize Submission
                    </GlassButton>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-cyan-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[2.5rem] bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-5xl text-cyan-400 mx-auto mb-16 shadow-[0_0_50px_rgba(34,211,238,0.2)]">
                <TiGlobeOutline />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Become a <span className="font-zentry italic text-cyan-400 text-8xl uppercase tracking-tighter block mt-4">Founder</span> of the Future
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                The 12-brand alliance is not just hiring employees; we are recruiting the architects of sovereign intelligence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   className="px-16 py-8 text-xl"
                   variant="cyan"
                   size="lg"
                   onClick={() => {}} // Placeholder
                 >
                   Join the Alliance
                 </GlassButton>
                 <GlassButton
                   className="px-16 py-8 text-xl flex items-center gap-4"
                   variant="gray"
                   size="lg"
                   onClick={() => window.open('/about', '_self')}
                 >
                   <div className="flex items-center gap-2">
                     Vision Statement <TiArrowRight className="text-2xl" />
                   </div>
                 </GlassButton>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-cyan-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Careers;
